import { useEffect, useState, useRef } from 'react';
import { Box, VStack, Text, Spinner, useMediaQuery, HStack, Badge, Divider } from 'native-base';
import { Toaster } from '../../../../components/toaster';
import { getSurveyFormsDetails } from '../../../../api/apis/workflow';
import NoDataFound from '../../../../components/noDataFound';
import { COLORS } from '../../../../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import CustomButton from '../../../../components/button';
import InputField from '../../../../components/input';
import Dropdown from '../../../../components/dropdown';
import CheckBox from '../../../../components/checkbox';
import SurveyFormModal from './SurveyFormModal';
import CryptoJS from 'crypto-js';
import config from '../../../../utils/config';

const SurveyForm = () => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });
  const [surveys, setSurveys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState(null);
  const modalRef = useRef();

  // Get current user information
  const getCurrentUser = () => {
    try {
      const bytes = CryptoJS.AES.decrypt(
        sessionStorage.getItem("content"),
        config.userKey
      );
      const jsonString = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(jsonString);
    } catch (error) {
      return null;
    }
  };

  const currentUser = getCurrentUser();

  // Check if current user can edit the survey
  const canEditSurvey = (survey) => {
    if (!currentUser || !survey.created_by_email) {
      return false;
    }
    // Compare current user email with survey creator email only
    return currentUser.email === survey.created_by_email;
  };

  useEffect(() => {
    getSurveyForms();
  }, []);

  const getSurveyForms = async (page = 1, addToExisting = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    
    try {
      const res = await getSurveyFormsDetails(page, 10);
      if (res.error) {
        toaster(res.message, 'error', 'error');
        return;
      }
      
      const surveyData = res.data?.data?.data || [];
      const pagination = res.data?.data?.pagination || {};
      
      if (addToExisting) {
        setSurveys(prev => [...prev, ...surveyData]);
      } else {
        setSurveys(surveyData);
      }
      
      setCurrentPage(page);
      setTotalPages(pagination?.pages || 1);
      setHasMore(page < (pagination?.pages || 1));
      setPagination(pagination);
    } catch (error) {
      toaster('Failed to load survey forms', 'error', 'error');
    }
    
    setIsLoading(false);
    setIsLoadingMore(false);
  };

  const handleCreateSurvey = () => {
    setEditData(null);
    setIsEditing(false);
    setShowCreateModal(true);
  };

  const handleEditSurvey = (survey) => {
    setEditData(survey);
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditData(null);
    setIsEditing(false);
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      getSurveyForms(currentPage + 1, true);
    }
  };

  const getQuestionType = (question) => {
    if (!question.options || question.options.length === 0) {
      return 'input';
    }
    
    // Check if it's true/false type
    if (question.options.length === 2 && 
        question.options.includes('True') && 
        question.options.includes('False')) {
      return 'true_false';
    }
    
    // Check if it's rating type (5 options with star names)
    if (question.options.length === 5 && 
        question.options.every((opt, i) => opt.includes(`${i + 1} Star`) || opt.includes(`${i + 1} Stars`))) {
      return 'rating';
    }
    
    // Otherwise it's input type
    return 'input';
  };

  const getQuestionTypeLabel = (question) => {
    const type = getQuestionType(question);
    switch (type) {
      case 'input':
        return 'Input Type';
      case 'true_false':
        return 'True/False';
      case 'rating':
        return 'Rating';
      default:
        return 'Input Type';
    }
  };

  const getQuestionTypeColor = (question) => {
    const type = getQuestionType(question);
    switch (type) {
      case 'input':
        return 'blue';
      case 'true_false':
        return 'orange';
      case 'rating':
        return 'purple';
      default:
        return 'blue';
    }
  };

  const renderRatingStars = (question) => {
    const maxStars = 5;
    const stars = [];
    
    for (let i = 1; i <= maxStars; i++) {
      const isSelected = question.options && question.options.length >= i;
      stars.push(
        <Text
          key={i}
          fontSize="xl"
          color={isSelected ? "yellow.400" : "gray.300"}
          mr={1}
        >
          ★
        </Text>
      );
    }
    
    return (
      <VStack space={2} alignItems="flex-start">
        <HStack space={1} alignItems="center">
          {stars}
        </HStack>
        <Text fontSize="sm" color="gray.500">
          Rating scale: 1 to {question.options?.length || 5} stars
        </Text>
      </VStack>
    );
  };

  if (isLoading) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <Spinner size="lg" color={COLORS.THEME_COLOR} />
      </Box>
    );
  }

  if (surveys?.length === 0) {
    return <NoDataFound dataName="Survey forms" />;
  }

  return (
    <VStack space={4} p={4}>
      <VStack space={4}>
        {surveys.map((survey) => (
          <Box
            key={survey.id || survey._id}
            p={4}
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            shadow="sm"
          >
            {/* Survey Header */}
            <VStack space={3}>
              <HStack justifyContent="space-between" alignItems="flex-start">
                <VStack flex={1} space={6}>
                  <HStack space={6} alignItems="center">
                    {survey.event_name && (
                      <Text color="gray.600" fontSize="md">
                        Event Name :  
                        <Text fontSize="lg" fontWeight="bold">
                          {' '}{survey.event_name}
                        </Text>
                      </Text>
                    )}
                    <Badge
                      colorScheme={survey.type === 'event' ? 'blue' : 'green'}
                      variant="subtle"
                      rounded="full"
                    >
                      {survey.type === 'event' ? 'Event' : 'MICE'}
                    </Badge>
                  </HStack>
                  {survey?.created_by_name && (
                    <Text color="gray.600" fontSize="md">
                      Created By :
                      <Text fontSize="lg" fontWeight="bold">
                        {' '}{survey?.created_by_name}
                      </Text>
                    </Text>
                  )}
                  <Text fontSize="lg" fontWeight="semibold">
                    {survey.title}
                  </Text>
                </VStack>
                  <CustomButton
                    text="Edit"
                    onPress={canEditSurvey(survey) ? () => handleEditSurvey(survey) : () => {}}
                    size="sm"
                    variant="outline"
                    isDisabled={!canEditSurvey(survey)}
                  />
              </HStack>

              <Divider />

              {/* Questions Section */}
              <VStack space={3}>
              <HStack alignItems="center"  style={{
                gap: '1rem'
              }}>

                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Questions ({survey.questions?.length || 0})
                </Text>
                </HStack>
                {survey.questions?.map((question, qIndex) => (
                  <Box key={qIndex} p={3} borderWidth={1} borderColor="gray.100" borderRadius="md" bg="gray.50">
                    <VStack space={2}>
                      <HStack justifyContent="space-between" alignItems="flex-start">
                        <VStack flex={1} space={1}>
                          <HStack space={2} alignItems="center">
                            <Text fontWeight="semibold" fontSize="xl">
                              Q{qIndex + 1}: {question.question}
                            </Text>
                            <Badge
                              colorScheme={getQuestionTypeColor(question)}
                              variant="subtle"
                              size="md"
                            >
                              {getQuestionTypeLabel(question)}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>
                      {getQuestionType(question) === 'rating' ? (
                        <VStack space={1} pl={4}>
                          <Text fontSize="md" color="gray.600" fontWeight="medium">
                            Rating Scale:
                          </Text>
                          {renderRatingStars(question)}
                        </VStack>
                      ) : question.options && question.options.length > 0 && getQuestionType(question) !== 'rating' && (
                        <VStack space={1} pl={4}>
                          <Text fontSize="md" color="gray.600" fontWeight="medium">
                            Options:
                          </Text>
                          {question.options
                            .map((option, oIndex) => ({
                              option,
                              weight: question.weights?.[oIndex] || 1,
                              originalIndex: oIndex
                            }))
                            .sort((a, b) => b.weight - a.weight) // Sort by weight descending
                            .map(({ option, weight, originalIndex }, sortedIndex) => (
                              <HStack key={originalIndex} space={2} alignItems="center">
                                <Box
                                  w={1.5}
                                  h={1.5}
                                  borderRadius="full"
                                  bg={
                                    weight === 5
                                      ? 'green.500'
                                      : weight === 4
                                      ? 'orange.500'
                                      : weight === 3
                                      ? 'yellow.500'
                                      : weight === 2
                                      ? 'red.500'
                                      : weight === 1
                                      ? 'gray.500'
                                      : 'gray.500'
                                  }
                                />
                                <Text fontSize="md" color="gray.700">{option}</Text>
                              </HStack>
                            ))}
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </VStack>
          </Box>
        ))}
      </VStack>

      {/* Pagination Info */}
      {pagination && surveys?.length > 0 && (
        <Box mt={4} textAlign="center">
          <Text fontSize="sm" color="gray.600">
            Page {pagination?.page} of {pagination?.pages} • Total {pagination?.total} surveys
          </Text>
        </Box>
      )}

      {/* Load More Button */}
      {hasMore && (
        <Box display="flex" justifyContent="center" alignSelf={'center'} py={4}>
          <CustomButton
            text={isLoadingMore ? "Loading..." : "Load More"}
            onPress={handleLoadMore}
            disabled={isLoadingMore}
            variant="outline"
          />
        </Box>
      )}

      <SurveyFormModal
        ref={modalRef}
        isOpen={showCreateModal}
        onClose={handleModalClose}
        editData={editData}
        isEditing={isEditing}
        onSuccess={() => {
          getSurveyForms(1);
        }}
      />
    </VStack>
  );
};

export default SurveyForm; 